import axios from "@/lib/axios";

class ProductService {
  constructor() {
    this.api = axios;
  }
  async getProducts(query) {
    const { data } = await this.api.get("/merchant-products", {
      params: query,
    });

    return data;
  }

  // Advanced filtering and sorting endpoint
  async getProductsAdvanced({ page = 1, size = 10, filter = [], sort = [] }) {
    // Build filter string: alan:operator:deger,alan:operator:deger
    const filterString = filter
      .filter((f) => f.value !== "" && f.value !== null && f.value !== undefined)
      .map((f) => `${f.field}:${f.operator}:${f.value}`)
      .join(",");

    // Build sort string: alan:direction,alan:direction
    const sortString = sort
      .filter((s) => s.field && s.direction)
      .map((s) => `${s.field}:${s.direction}`)
      .join(",");

    const params = { page, size };
    if (filterString) params.filter = filterString;
    if (sortString) params.sort = sortString;

    const { data } = await this.api.get("/merchant-products/advanced", {
      params,
    });

    return data;
  }
  async deleteProduct(id) {
    await this.api.delete(`/merchant-products/${id}`);
  }

  async getProduct(id) {
    const { data } = await this.api.get(`/merchant-products/${id}`);

    return data;
  }

  // get documents by product id
  async getDocuments(id) {
    const { data } = await this.api.get(`/merchant-products/${id}/documents`);

    return data?.merchantProductDocuments;
  }

  // upload document for product by product id
  async uploadDocument(id, payload, formData) {
    const { data } = await this.api.put(
      `/merchant-products/${id}/upload-document`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        params: {
          name: payload.name,
          type: payload.type,
        },
      },
    );

    return data;
  }

  // select document for product by product id
  async selectDocument(id, documentId) {
    const { data } = await this.api.put(
      `/merchant-products/${id}/add-document`,
      null,
      {
        params: {
          documentId,
        },
      },
    );

    return data;
  }

  async removeDocument(id) {
    const { data } = await this.api.put(
      `/merchant-products/${id}/unlinked-user-manual-document`,
    );

    return data;
  }

  async getDocumentList(query) {
    const { data } = await this.api.get(`/merchant-product-documents/name`, {
      params: {
        ...query,
      },
    });

    return data;
  }

  async exportProducts(query) {
    // BE küçük kataloglarda CSV'yi doğrudan döner (200 + text/csv), eşiğin üstünde
    // gövdesiz 202 döner ve dosyayı maille gönderir. İki durumu ayırt edebilmek için
    // yanıtın tamamı döndürülür.
    const res = await this.api.get("/merchant-products/csv", {
      params: query,
      responseType: "blob",
    });

    return res;
  }

  async importProductsFromSource(merchantProductSource, data) {
    const res = await this.api.post(
      `/merchant-products/bulk?merchantProductSource=${merchantProductSource}`,
      data,
    );

    return res;
  }

  async updateProduct(product) {
    const data = await this.api.put(
      `/merchant-products/${product.id}`,
      product,
    );

    return data;
  }

  async deleteProductImage(id) {
    const data = await this.api.delete(
      `/merchant-products/image?merchantProductId=${id}`,
    );

    return data;
  }

  async postProductImage(id, image) {
    const data = await this.api.post(
      `/merchant-products/image?merchantProductId=${id}`,
      image,
    );

    return data;
  }

  async getMainCategories() {
    const data = await this.api.get(`/main-categories`, {});

    return data;
  }

  async getCategories(id) {
    const data = await this.api.get(`/categories`, {
      params: { mainCategoryId: id },
    });

    return data;
  }

  async getSubCategories(id) {
    const data = await this.api.get(`/sub-categories`, {
      params: { categoryId: id },
    });

    return data;
  }

  async getSubCategoryBrands(id) {
    const data = await this.api.get(`/sub-category-brands`, {
      params: { subCategoryId: id },
    });

    return data;
  }

  async getMainCategoriesAutoComplete() {
    const data = await this.api.get(
      `/merchant-products/auto-complete/main-categories`,
      {},
    );

    return { data: data.data.content || [] };
  }

  async getCategoriesAutoComplete() {
    const data = await this.api.get(
      `/merchant-products/auto-complete/categories`,
      {},
    );

    return { data: data.data.content || [] };
  }

  async getSubCategoriesAutoComplete() {
    const data = await this.api.get(
      `/merchant-products/auto-complete/sub-categories`,
      {},
    );

    return { data: data.data.content || [] };
  }

  async getSubCategoryBrandsAutoComplete() {
    const data = await this.api.get(
      `/merchant-products/auto-complete/brands`,
      {},
    );

    return { data: data.data.content || [] };
  }

  async autocompleteMainCategories(query) {
    const { data } = await this.api.get(
      "/merchant-products/auto-complete/main-categories",
      {
        params: { query },
      },
    );
    return data.content || [];
  }

  async autocompleteCategories(query) {
    const { data } = await this.api.get(
      "/merchant-products/auto-complete/categories",
      {
        params: { query },
      },
    );
    return data.content || [];
  }

  async autocompleteSubCategories(query) {
    const { data } = await this.api.get(
      "/merchant-products/auto-complete/sub-categories",
      {
        params: { query },
      },
    );
    return data.content || [];
  }

  async autocompleteBrands(query) {
    const { data } = await this.api.get(
      "/merchant-products/auto-complete/brands",
      {
        params: { query },
      },
    );
    return data.content || [];
  }

  async deleteUnusedMainCategories() {
    const data = await this.api.delete(
      `/category-management/main-categories/unused`,
    );
    return data;
  }

  async createProduct(product) {
    const data = await this.api.post(`/merchant-products`, product);

    return data;
  }

  /**
   * EPS-4970 revize: "Satış Ekle" akışının ürün adımı. Ana kategori + marka + ürün
   * adı + fiyattan ürünü bulur, yoksa oluşturur ve ürün DTO'sunu (id, productCode,
   * kategori id'leri) döner.
   *
   * Ürün kodu bilinçli olarak gönderilmez: {@code (merchant_id, product_code)}
   * tekilliğini yalnızca product-api garanti edebilir. İstek idempotent — aynı
   * veriyle ikinci çağrı mevcut ürünü döner, ürün listesine kopya kayıt eklenmez.
   */
  async resolveProductForSale(payload) {
    const { data } = await this.api.post("/merchant-products/for-sale", payload);

    return data;
  }

  async sendWarrantyIndividual(product) {
    const { billingFile, merchantProductIds, ...queryParams } = product;
    console.log(billingFile);
    const formData = new FormData();
    formData.append("billingFile", billingFile);

    const params = new URLSearchParams();
    merchantProductIds.forEach((id) => params.append("merchantProductIds", id));
    for (const [key, value] of Object.entries(queryParams)) {
      params.append(key, value);
    }

    const data = await this.api.post(
      `/individual-customers/warranty`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        params,
      },
    );

    return data;
  }

  async sendWarrantyCorporate(product) {
    const { billingFile, merchantProductIds, ...queryParams } = product;

    const formData = new FormData();
    formData.append("billingFile", billingFile);

    const params = new URLSearchParams();
    merchantProductIds.forEach((id) => params.append("merchantProductIds", id));
    for (const [key, value] of Object.entries(queryParams)) {
      params.append(key, value);
    }

    const data = await this.api.post(
      `/corporate-customers/warranty`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        params,
      },
    );

    return data;
  }

  // EPS-4850: Kategori resmi/serino bilgisi yönetimi
  // Resim CDN'e yüklenir, dönen URL ile kategori güncellenir.
  async uploadFile(file) {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await this.api.post("/files", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    // Beklenen response: { path, url }
    return data;
  }

  async updateMainCategoryImage(mainCategoryId, imageUrl) {
    const { data } = await this.api.patch(
      `/main-categories/${mainCategoryId}/image`,
      { imageUrl },
    );
    return data;
  }

  async deleteMainCategoryImage(mainCategoryId) {
    const { data } = await this.api.delete(
      `/main-categories/${mainCategoryId}/image`,
    );
    return data;
  }

  async updateCategoryImage(categoryId, imageUrl) {
    const { data } = await this.api.patch(
      `/categories/${categoryId}/image`,
      { imageUrl },
    );
    return data;
  }

  async deleteCategoryImage(categoryId) {
    const { data } = await this.api.delete(
      `/categories/${categoryId}/image`,
    );
    return data;
  }

  async updateCategorySerialNoInfo(categoryId, serialNoInfo) {
    const { data } = await this.api.patch(
      `/categories/${categoryId}/serial-no-info`,
      { serialNoInfo },
    );
    return data;
  }

  async updateSubCategoryImage(subCategoryId, imageUrl) {
    const { data } = await this.api.patch(
      `/sub-categories/${subCategoryId}/image`,
      { imageUrl },
    );
    return data;
  }

  async deleteSubCategoryImage(subCategoryId) {
    const { data } = await this.api.delete(
      `/sub-categories/${subCategoryId}/image`,
    );
    return data;
  }

  async updateSubCategoryBrandImage(subCategoryBrandId, imageUrl) {
    const { data } = await this.api.patch(
      `/sub-category-brands/${subCategoryBrandId}/image`,
      { imageUrl },
    );
    return data;
  }

  async deleteSubCategoryBrandImage(subCategoryBrandId) {
    const { data } = await this.api.delete(
      `/sub-category-brands/${subCategoryBrandId}/image`,
    );
    return data;
  }
}

export default new ProductService();
